import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Modal, Form, Select, DatePicker, InputNumber, Input, Radio, AutoComplete,
  Row, Col, Typography, Divider, Card, message, Spin,
} from 'antd';
import {
  ShoppingCartOutlined, UserOutlined, PhoneOutlined, DollarOutlined,
  CalendarOutlined, TeamOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../../api/axios';

const { Text } = Typography;
const { TextArea } = Input;

const formatPrice = (p) => {
  if (!p && p !== 0) return '0';
  return Number(p).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

export default function NewOrderModal({ open, onClose, onCreated }) {
  const [form] = Form.useForm();
  const [services, setServices] = useState([]);
  const [options, setOptions] = useState(null); // { isRestaurant, service_tariffs, halls }
  const [optLoading, setOptLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Mijoz
  const [clientMode, setClientMode] = useState('new'); // 'existing' | 'new'
  const [custOptions, setCustOptions] = useState([]);
  const [selectedCustId, setSelectedCustId] = useState(null);
  const searchRef = useRef(null);

  const serviceId = Form.useWatch('services_id', form);
  const tariffId = Form.useWatch('tariff_id', form);
  const hallId = Form.useWatch('hall_id', form);
  const quantity = Form.useWatch('quantity', form);

  // Xizmatlar ro'yxati
  useEffect(() => {
    if (!open) return;
    api.get('/services')
      .then(res => setServices(res.data?.services || []))
      .catch(() => message.error('Xizmatlar yuklanmadi'));
  }, [open]);

  // Modal ochilganda reset
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ quantity: 1, status: 'pending' });
      setOptions(null);
      setClientMode('new');
      setSelectedCustId(null);
      setCustOptions([]);
    }
  }, [open, form]);

  // Xizmat tanlanganda variantlar (tarif/zal)
  useEffect(() => {
    if (!serviceId) { setOptions(null); return; }
    setOptLoading(true);
    setOptions(null);
    form.setFieldsValue({ tariff_id: undefined, hall_id: undefined, summa: undefined });
    api.get(`/orders/booking-options/${serviceId}`)
      .then(res => {
        const d = res.data?.data;
        setOptions(d);
        // restoran bo'lmasa mehmon soni 1
        if (!d?.isRestaurant) form.setFieldsValue({ quantity: 1 });
      })
      .catch(() => message.error('Tarif variantlari yuklanmadi'))
      .finally(() => setOptLoading(false));
  }, [serviceId, form]);

  const isRestaurant = options?.isRestaurant;

  // Tanlangan zalning event_type lari
  const hallEventTypes = useMemo(() => {
    if (!isRestaurant || !hallId || !options?.halls) return [];
    const h = options.halls.find(x => x.id === hallId);
    return h?.event_types || [];
  }, [isRestaurant, hallId, options]);

  // Tanlangan tarif obyekti + 1 birlik narxi
  const selectedTariff = useMemo(() => {
    if (!tariffId) return null;
    if (isRestaurant) return hallEventTypes.find(t => t.id === tariffId) || null;
    return (options?.service_tariffs || []).find(t => t.id === tariffId) || null;
  }, [tariffId, isRestaurant, hallEventTypes, options]);

  // Avto taklif narx: restoran = narx × mehmon, aks holda tarif narxi
  const suggestedPrice = useMemo(() => {
    if (!selectedTariff) return null;
    const unit = Number(selectedTariff.price) || 0;
    return isRestaurant ? unit * (Number(quantity) || 1) : unit;
  }, [selectedTariff, isRestaurant, quantity]);

  // Tarif/mehmon o'zgarsa narx maydonini avto-to'ldirish (provider keyin o'zgartira oladi)
  useEffect(() => {
    if (suggestedPrice != null) form.setFieldsValue({ summa: suggestedPrice });
  }, [suggestedPrice, form]);

  // Mijoz qidirish (debounce)
  const handleSearch = useCallback((val) => {
    if (searchRef.current) clearTimeout(searchRef.current);
    if (!val || val.length < 3) { setCustOptions([]); return; }
    searchRef.current = setTimeout(() => {
      api.get(`/orders/search-customers?q=${encodeURIComponent(val)}`)
        .then(res => {
          setCustOptions((res.data?.data || []).map(u => ({
            value: String(u.id),
            label: `${u.fullname} — ${u.phone}`,
          })));
        })
        .catch(() => setCustOptions([]));
    }, 350);
  }, []);

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    if (isRestaurant && !values.hall_id) {
      message.warning('Zalni tanlang');
      return;
    }
    if (clientMode === 'existing' && !selectedCustId) {
      message.warning('Mavjud mijozni tanlang');
      return;
    }

    const d = dayjs(values.date);
    const payload = {
      services_id: values.services_id,
      day: d.date(),
      month: d.month() + 1, // 1-12
      year: d.year(),
      start_datetime: values.start_datetime,
      end_datetime: values.end_datetime,
      quantity: isRestaurant ? (values.quantity || 1) : 1,
      summa: values.summa,
      note: values.note || null,
      status: values.status || 'pending',
    };
    // Tarif: restoran -> extra_id (event_type), aks holda service_tariff_id
    if (isRestaurant) payload.extra_id = values.tariff_id || null;
    else payload.service_tariff_id = values.tariff_id || null;

    // Mijoz
    if (clientMode === 'existing') {
      payload.client_user_id = Number(selectedCustId);
    } else {
      payload.client_name = values.client_name || null;
      payload.client_phone = values.client_phone || null;
    }

    setSubmitting(true);
    api.post('/orders', payload)
      .then(() => {
        message.success('Buyurtma qo\'shildi');
        onCreated?.();
        onClose();
      })
      .catch(err => {
        const msg = err.response?.data?.message || 'Buyurtma yaratishda xatolik';
        message.error(msg);
      })
      .finally(() => setSubmitting(false));
  };

  const hourOptions = Array.from({ length: 19 }, (_, i) => i + 6)
    .map(h => ({ value: h, label: `${h}:00` }));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Buyurtma qo'shish"
      cancelText="Bekor"
      confirmLoading={submitting}
      width={640}
      destroyOnHidden
      title={<span><ShoppingCartOutlined style={{ marginRight: 8, color: '#667eea' }} />Yangi buyurtma</span>}
    >
      <Form form={form} layout="vertical" requiredMark={false} style={{ marginTop: 8 }}>
        <Form.Item name="services_id" label="Xizmat" rules={[{ required: true, message: 'Xizmatni tanlang' }]}>
          <Select
            placeholder="Xizmatni tanlang"
            showSearch
            optionFilterProp="label"
            options={services.map(s => ({ value: s.id, label: `${s.name} (${s.service_type_name})` }))}
          />
        </Form.Item>

        {optLoading && <div style={{ textAlign: 'center', padding: 12 }}><Spin /></div>}

        {options && (
          <>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="date" label={<span><CalendarOutlined /> Sana</span>} rules={[{ required: true, message: 'Sanani tanlang' }]}>
                  <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY"
                    disabledDate={(c) => c && c < dayjs().startOf('day')} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="start_datetime" label="Boshlanish" rules={[{ required: true, message: 'Vaqt' }]}>
                  <Select placeholder="—" options={hourOptions} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="end_datetime" label="Tugash" rules={[{ required: true, message: 'Vaqt' },
                  ({ getFieldValue }) => ({
                    validator(_, v) {
                      const s = getFieldValue('start_datetime');
                      if (v == null || s == null || v > s) return Promise.resolve();
                      return Promise.reject(new Error('Tugash > boshlanish'));
                    },
                  })]}>
                  <Select placeholder="—" options={hourOptions} />
                </Form.Item>
              </Col>
            </Row>

            {/* Restoran: zal tanlash */}
            {isRestaurant && (
              <Form.Item name="hall_id" label="Zal" rules={[{ required: true, message: 'Zalni tanlang' }]}>
                <Select
                  placeholder="Zalni tanlang"
                  onChange={() => form.setFieldsValue({ tariff_id: undefined })}
                  options={(options.halls || []).map(h => ({
                    value: h.id, label: h.places ? `${h.name} (${h.places} kishi)` : h.name,
                  }))}
                />
              </Form.Item>
            )}

            {/* Tarif tanlash */}
            <Form.Item name="tariff_id" label={isRestaurant ? "Tarif (1 kishi narxi)" : "Tarif"}>
              <Select
                placeholder={isRestaurant ? 'Avval zalni tanlang' : 'Tarif tanlang'}
                allowClear
                disabled={isRestaurant && !hallId}
                options={(isRestaurant ? hallEventTypes : (options.service_tariffs || [])).map(t => ({
                  value: t.id, label: `${t.name} — ${formatPrice(t.price)} so'm${isRestaurant ? '/kishi' : ''}`,
                }))}
                notFoundContent={isRestaurant ? 'Bu zal uchun tarif yo\'q' : 'Tarif kiritilmagan'}
              />
            </Form.Item>

            <Row gutter={12}>
              {isRestaurant && (
                <Col span={12}>
                  <Form.Item name="quantity" label={<span><TeamOutlined /> Mehmonlar soni</span>}
                    rules={[{ required: true, message: 'Soni' }]}>
                    <InputNumber min={1} max={10000} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              )}
              <Col span={isRestaurant ? 12 : 24}>
                <Form.Item name="summa" label={<span><DollarOutlined /> Narx (so'm) — kelishilgan</span>}
                  rules={[{ required: true, message: 'Narxni kiriting' }]}>
                  <InputNumber min={0} style={{ width: '100%' }}
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                    parser={v => v.replace(/\s/g, '')} />
                </Form.Item>
              </Col>
            </Row>
            {suggestedPrice != null && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: -8, marginBottom: 8 }}>
                Tavsiya etilgan narx: <b>{formatPrice(suggestedPrice)} so'm</b> (o'zgartirishingiz mumkin)
              </Text>
            )}

            <Divider style={{ margin: '4px 0 14px' }} />

            {/* Mijoz */}
            <Form.Item label="Mijoz" style={{ marginBottom: 8 }}>
              <Radio.Group value={clientMode} onChange={e => setClientMode(e.target.value)} optionType="button" buttonStyle="solid">
                <Radio.Button value="new">Yangi mijoz</Radio.Button>
                <Radio.Button value="existing">Mavjud mijoz</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {clientMode === 'new' ? (
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="client_name" label="Ism" rules={[{ required: true, message: 'Mijoz ismi' }]}>
                    <Input prefix={<UserOutlined />} placeholder="Mijoz ismi" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="client_phone" label="Telefon">
                    <Input prefix={<PhoneOutlined />} placeholder="+998..." />
                  </Form.Item>
                </Col>
              </Row>
            ) : (
              <Form.Item label="Mijozni qidirish (ism yoki telefon)" required>
                <AutoComplete
                  options={custOptions}
                  onSearch={handleSearch}
                  onSelect={(val) => setSelectedCustId(val)}
                  onChange={() => setSelectedCustId(null)}
                  placeholder="Kamida 3 ta belgi kiriting"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            )}

            <Form.Item name="note" label="Izoh">
              <TextArea rows={2} placeholder="Qo'shimcha izoh (ixtiyoriy)" />
            </Form.Item>

            {/* Xulosa */}
            <Card size="small" style={{ borderRadius: 8, background: 'var(--qk-bg-soft)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Jami summa:</Text>
                <Text strong style={{ fontSize: 16, color: '#22c55e' }}>
                  {formatPrice(form.getFieldValue('summa'))} so'm
                </Text>
              </div>
            </Card>
          </>
        )}
      </Form>
    </Modal>
  );
}
